import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  alpha
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Inventory as InventoryIcon,
  Construction as MaterialsIcon,
  LocationOn as SitesIcon,
  People as UsersIcon,
  Assessment as ReportsIcon,
  Settings as SettingsIcon,
  Palette as PaletteIcon,
  Logout as LogoutIcon,
  AccountCircle,
  KeyboardArrowDown,
  SwapHoriz,
  Email as EmailIcon,
  AssignmentInd as ChangeRequestIcon,
  HelpOutline as SupportIcon,
  ConfirmationNumber as TicketIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import FloatingHelpButton from './FloatingHelpButton';

const drawerWidth = 240;

const DashboardLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const { user, logout, isAdmin, isManager } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
    { text: 'Material Logs', icon: <MaterialsIcon />, path: '/materials' },
    { text: 'Inventory', icon: <InventoryIcon />, path: '/inventory' },
    { text: 'Sites', icon: <SitesIcon />, path: '/sites' },
    { text: 'Transfers', icon: <SwapHoriz />, path: '/transfers' },
    { text: 'Analytics', icon: <ReportsIcon />, path: '/reports' },
    {
      text: 'My Profile',
      icon: <AccountCircleIcon />,
      path: '/profile',
      roles: ['admin', 'manager', 'supervisor']
    },
    // Support — visible to all logged-in users
    { text: 'Support', icon: <SupportIcon />, path: '/support' }
  ];

  // Manager/Supervisor specific menu items
  if (user?.role === 'manager' || user?.role === 'supervisor') {
    menuItems.push({
      text: 'My Change Requests',
      icon: <ChangeRequestIcon />,
      path: '/my-change-requests'
    });
  }

  if (isAdmin || isManager) {
    menuItems.push({ text: 'Team', icon: <UsersIcon />, path: '/users' });
  }

  if (isAdmin) {
    menuItems.push({
      text: 'Change Requests',
      icon: <ChangeRequestIcon />,
      path: '/admin/change-requests'
    });
    menuItems.push({
      text: 'Tickets (Admin)',
      icon: <TicketIcon />,
      path: '/admin/tickets'
    });
    menuItems.push({
      text: 'Company Branding',
      icon: <PaletteIcon />,
      path: '/settings'
    });
    menuItems.push({
      text: 'System Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings'
    });
    menuItems.push({
      text: 'Email Settings',
      icon: <EmailIcon />,
      path: '/admin/email'
    });
  }

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'white' }}>
      {/* Sidebar Header - BIGGER LOGO */}
      <Box sx={{
        p: 3,
        pb: 2,
        borderBottom: '1px solid #F0F0F0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: settings?.logoUrl ? 'column' : 'row' }}>
          {/* ✅ BIGGER LOGO - Now 80x80 */}
          {settings?.logoUrl && settings?.showLogoInHeader && (
            <Box
              component="img"
              src={settings.logoUrl}
              alt="Company Logo"
              sx={{
                height: 80,
                width: 80,
                objectFit: 'contain',
                cursor: 'pointer'
              }}
              onClick={() => navigate('/dashboard')}
            />
          )}

          {/* Company Name and Tagline */}
          {settings?.showCompanyName && (
            <Box sx={{ textAlign: settings?.logoUrl ? 'center' : 'left', width: '100%' }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  fontSize: '1.1rem',
                  color: '#1A1A1A',
                  lineHeight: 1.2,
                  cursor: 'pointer'
                }}
                onClick={() => navigate('/dashboard')}
              >
                {settings?.companyName || 'Material Tracker'}
              </Typography>
              {settings?.tagline && (
                <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.7rem', display: 'block', mt: 0.5 }}>
                  {settings.tagline}
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* Menu Items - Clean Minimal Style */}
      <List sx={{ flexGrow: 1, px: 2, py: 2 }}>
        {menuItems.map((item) => {
          const isSelected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={isSelected}
                onClick={() => {
                  navigate(item.path);
                  setMobileOpen(false);
                }}
                sx={{
                  borderRadius: 1.5,
                  px: 2,
                  py: 1.2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#F5F5F5',
                  },
                  '&.Mui-selected': {
                    bgcolor: alpha(settings?.primaryColor || '#64A8ED', 0.08),
                    color: settings?.primaryColor || '#64A8ED',
                    '&:hover': {
                      bgcolor: alpha(settings?.primaryColor || '#64A8ED', 0.12),
                    },
                    '& .MuiListItemIcon-root': {
                      color: settings?.primaryColor || '#64A8ED',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{
                  color: isSelected ? (settings?.primaryColor || '#64A8ED') : '#757575',
                  minWidth: 40
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: '0.875rem',
                    color: isSelected ? 'inherit' : '#424242'
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid #F0F0F0', bgcolor: '#FAFAFA' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ fontSize: '0.7rem' }}>
          © 2026 {settings?.companyName || 'HyLif Innovations'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', bgcolor: '#FAFAFA', minHeight: '100vh' }}>
      {/* App Bar - Clean White Header */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'white',
          borderBottom: '1px solid #E8E8E8',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: { xs: 56, sm: 64 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                display: { sm: 'none' },
                color: 'text.primary'
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Page Title */}
            <Typography variant="h6" color="text.primary" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
              {menuItems.find(item => item.path === location.pathname)?.text || 'Dashboard'}
            </Typography>
          </Box>

          {/* User Profile Menu - Minimal Style */}
          <Box
            onClick={handleMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              cursor: 'pointer',
              px: 1.5,
              py: 0.5,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#F5F5F5'
              }
            }}
          >
            <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1A1A1A', fontSize: '0.875rem' }}>
                {user?.name}
              </Typography>
              <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.7rem' }}>
                {user?.role}
              </Typography>
            </Box>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: settings?.primaryColor || '#64A8ED',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              {user?.name?.charAt(0)}
            </Avatar>
            <KeyboardArrowDown sx={{ fontSize: 18, color: '#757575' }} />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 2,
              sx: {
                mt: 1.5,
                minWidth: 180,
                borderRadius: 2,
                border: '1px solid #E8E8E8',
                '& .MuiMenuItem-root': {
                  borderRadius: 1,
                  mx: 1,
                  my: 0.5,
                  fontSize: '0.875rem'
                }
              }
            }}
          >
            <MenuItem disabled sx={{ opacity: '1 !important' }}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                <Typography variant="body2" fontWeight={600}>
                  {user?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.role}
                </Typography>
              </ListItemText>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        {/* Mobile drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none'
            }
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRight: '1px solid #E8E8E8'
            }
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: { xs: 7, sm: 8 },
          minHeight: '100vh'
        }}
      >
        <Outlet />
      </Box>

      {/* Floating Help Button — always visible */}
      <FloatingHelpButton />
    </Box>
  );
};

export default DashboardLayout;